import { useState } from "react";
import { ChevronDown } from "lucide-react";
import Seo from "../lib/seo";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";
import PetitionCTA from "../components/PetitionCTA";

const FAQS = [
    { q: "Is my donation tax-deductible?", a: "Eligible donations may become tax deductible as permitted by law." },
    { q: "Where does my donation go?", a: "Donations help support CPR awareness initiatives, emergency preparedness education, advocacy efforts, educational resources, community outreach, and awareness campaigns." },
    { q: "Can I make recurring donations?", a: "Yes. Supporters can choose one-time or recurring monthly donations to help sustain the foundation's mission." },
    { q: "Can businesses or organizations sponsor JC Law?", a: "Yes. We welcome partnerships and sponsorship opportunities with businesses, organizations, and community leaders who support our mission." },
    { q: "Is the JC Law Foundation officially registered?", a: "Yes, JC Law Foundation is established as a nonprofit organization dedicated to public safety awareness and emergency preparedness advocacy." },
    { q: "Is this foundation focused on blame?", a: "No. The mission of the JC Law Foundation is centered on awareness, education, preparedness, advocacy, and empowering communities through life-saving resources and information." },
    { q: "Why does this mission matter?", a: "The JC Law Foundation believes preparedness, education, and immediate action can make a meaningful difference during emergencies. Through awareness and advocacy, we hope to empower communities and honor JC's legacy through purpose and impact." },
    { q: "How can I contact the foundation?", a: "You can reach us through the Contact page for questions, support inquiries, partnership opportunities, or media requests." },
    { q: "How can I share JC's story?", a: "Supporters can help by sharing the foundation's mission, educational resources, and advocacy efforts on social media and within their communities." },
];

export default function FAQPage() {
    const [open, setOpen] = useState(0);

    const schema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
    };

    return (
        <>
            <Seo
                title="FAQ — Frequently Asked Questions"
                description="Answers to common questions about the JC Law Foundation, donations, partnership opportunities, advocacy, and how to share JC's story."
                path="/faq"
                schema={schema}
            />

            <PageHeader
                overline="Frequently Asked Questions"
                title="Answers, with care."
                subtitle="Below are the answers to questions we hear most often. Have something else on your mind? Reach out to us."
            />

            <section className="bg-editorial py-24 md:py-32" data-testid="faq-section">
                <div className="max-w-[900px] mx-auto px-6 lg:px-10">
                    <div className="divide-y divide-editorial-border border-y border-editorial-border">
                        {FAQS.map((f, i) => {
                            const isOpen = open === i;
                            return (
                                <Reveal key={i} delay={i * 0.03}>
                                    <div data-testid={`faq-item-${i}`}>
                                        <button
                                            type="button"
                                            onClick={() => setOpen(isOpen ? -1 : i)}
                                            aria-expanded={isOpen}
                                            data-testid={`faq-toggle-${i}`}
                                            className="w-full py-7 flex items-start justify-between gap-6 text-left group"
                                        >
                                            <h3 className="font-serif text-xl md:text-2xl text-navy-900 group-hover:text-coral transition-colors leading-snug">
                                                {f.q}
                                            </h3>
                                            <ChevronDown className={`w-5 h-5 text-navy-900 flex-shrink-0 mt-2 transition-transform duration-300 ${isOpen ? "rotate-180 text-coral" : ""}`} />
                                        </button>
                                        <div
                                            className={`grid transition-all duration-500 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100 pb-8" : "grid-rows-[0fr] opacity-0"}`}
                                        >
                                            <div className="overflow-hidden">
                                                <p className="text-lg text-navy-800/80 leading-relaxed max-w-3xl">
                                                    {f.a}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            <PetitionCTA />
        </>
    );
}
